import { FC, ReactNode, MouseEventHandler, FormEventHandler } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';

type FormDialogProps = {
  open: boolean;
  onClose: DialogProps['onClose'];
  title: ReactNode;
  children: ReactNode;
  /** Button click submit (when not using onFormSubmit) */
  onSubmit?: MouseEventHandler<HTMLButtonElement> | (() => void);
  /** Native form submit; wraps content + actions in <form> */
  onFormSubmit?: FormEventHandler<HTMLFormElement>;
  submitText?: ReactNode;
  cancelText?: ReactNode;
  isSubmitLoading?: boolean;
  submitDisabled?: boolean;
  maxWidth?: DialogProps['maxWidth'];
  fullWidth?: boolean;
  /** Hide footer when the form embeds its own actions */
  hideActions?: boolean;
  dialogProps?: Omit<DialogProps, 'open' | 'onClose' | 'children'>;
};

export const FormDialog: FC<FormDialogProps> = ({
  open,
  onClose,
  title,
  children,
  onSubmit,
  onFormSubmit,
  submitText,
  cancelText,
  isSubmitLoading = false,
  submitDisabled = false,
  maxWidth = 'sm',
  fullWidth = true,
  hideActions = false,
  dialogProps,
}) => {
  const { t } = useTranslation();

  const handleCancel: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (onClose) onClose(event, 'escapeKeyDown');
  };

  const actions = !hideActions ? (
    <DialogActions
      sx={{
        px: 3,
        pb: 2.5,
        pt: 1,
        gap: 1,
        flexDirection: { xs: 'column-reverse', sm: 'row' },
      }}
    >
      <Button
        fullWidth
        variant="outlined"
        color="inherit"
        disabled={isSubmitLoading}
        onClick={handleCancel}
        type="button"
      >
        {cancelText ?? t('cancel')}
      </Button>
      {(onSubmit || onFormSubmit) && (
        <LoadingButton
          fullWidth
          variant="contained"
          loading={isSubmitLoading}
          disabled={submitDisabled}
          type={onFormSubmit ? 'submit' : 'button'}
          onClick={
            onFormSubmit
              ? undefined
              : (onSubmit as MouseEventHandler<HTMLButtonElement>)
          }
          sx={{ m: '0 !important' }}
        >
          {submitText ?? t('confirm')}
        </LoadingButton>
      )}
    </DialogActions>
  ) : null;

  const body = (
    <>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '8px !important',
        }}
      >
        {children}
      </DialogContent>
      {actions}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      {...dialogProps}
    >
      <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>{title}</DialogTitle>
      {onFormSubmit ? (
        <form autoComplete="off" onSubmit={onFormSubmit} noValidate>
          {body}
        </form>
      ) : (
        body
      )}
    </Dialog>
  );
};
